<template>
    <div class="partner-search-container">
        <div class="search-section">
                <div class="left-content">
                    <h4>Step 1: Search for Existing Vendor</h4>
                    <p class="search-description">Search for an existing vendor across all channels to avoid duplicates.</p>
                    <div class="search-input-wrapper">
                        <i class="pi pi-search search-icon" :class="{ 'searching': isSearching }" />
                        <InputText
                            v-model="searchQuery"
                            placeholder="Search term"
                            @input="handleSearch"
                            class="search-input"
                        />
                    </div>
                </div>
        </div>

        <div v-if="isSearching" class="search-loading">
            <ProgressBar mode="indeterminate" style="height: 4px"></ProgressBar>
            <p>Searching...</p>
        </div>

        <div v-else-if="searchResults.length > 0" class="search-results">
            <h5>Search Results ({{ searchResults.length }})</h5>
            <div class="results-list">
                <div 
                    v-for="(partner, index) in searchResults" 
                    :key="index"
                    class="result-item"
                    @click="selectPartner(partner)"
                >
                    <div class="partner-info">
                        <div class="partner-name">{{ partner.name }}</div>
                    </div>
                    <div class="select-indicator">
                        <i class="pi pi-arrow-right"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- Show "Create New Partner" button when there are search results but no exact matches -->
        <div v-if="hasSearched && searchQuery.trim() && searchResults.length > 0 && !hasExactMatch" class="create-new-section">
            <!-- Debug: hasExactMatch = {{ hasExactMatch }} -->
            <div class="no-results-content">
                <p class="no-results-title">No exact matches found for "{{ searchQuery }}"</p>
                <p class="no-results-text">You can proceed to create a new vendor account.</p>
                <Button 
                    label="Create New Vendor" 
                    class="p-button-primary single-line-button" 
                    @click="proceedToCreate"
                    :disabled="!searchQuery.trim()"
                />
            </div>
        </div>


        <div v-else-if="hasSearched && searchQuery.trim() && !hasExactMatch" class="no-results">
            <div class="no-results-content">
                <i class="pi pi-search" style="font-size: 3rem; color: #ddd;"></i>
                <p class="no-results-title">No vendors found "{{ searchQuery }}"</p>
                <p class="no-results-text">You can proceed to create a new vendor account.</p>
                <Button 
                    label="Create New Vendor" 
                    class="p-button-primary single-line-button" 
                    @click="proceedToCreate"
                    :disabled="!searchQuery.trim()"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch, computed } from 'vue';
import InputText from 'primevue/inputtext';
import ProgressBar from 'primevue/progressbar';
import Button from 'primevue/button';
import AccountMangementService from '../service/AccountManagementService';

export default {
    name: 'PartnerSearch',
    components: {
        InputText,
        ProgressBar,
        Button
    },
    emits: ['partner-selected', 'proceed-to-create'],
    setup(props, { emit }) {
        const searchQuery = ref('');
        const searchResults = ref([]);
        const isSearching = ref(false);
        const hasSearched = ref(false);
        const accountManagementService = new AccountMangementService();

        // Debounced search function
        let searchTimeout = null;
        const handleSearch = () => {
            clearTimeout(searchTimeout);
            if (searchQuery.value.trim().length < 2) {
                searchResults.value = [];
                hasSearched.value = false;
                return;
            }

            searchTimeout = setTimeout(async () => {
                await performSearch();
            }, 500);
        };

        const performSearch = async () => {
            if (!searchQuery.value.trim()) return;

            isSearching.value = true;
            hasSearched.value = true;

            try {
                const response = await accountManagementService.searchAllPartners(searchQuery.value);
                
                // Handle undefined response
                if (!response) {
                    console.error('Search failed: No response received');
                    searchResults.value = [];
                    return;
                }
                
                // Check if response has status property
                if (response.status === 200) {
                    searchResults.value = response.data || [];
                    console.log("Search Res >>",searchResults)
                } else {
                    searchResults.value = [];
                    console.error('Search failed:', response);
                }
            } catch (error) {
                console.error('Error performing search:', error);
                searchResults.value = [];
                // You could emit an error event here to show a toast notification
            } finally {
                isSearching.value = false;
            }
        };

        const selectPartner = (partner) => {
            emit('partner-selected', partner);
        };

        const proceedToCreate = () => {
            emit('proceed-to-create', searchQuery.value);
        };

        // Computed property to check for exact matches
        const hasExactMatch = computed(() => {
            if (!searchQuery.value.trim() || searchResults.value.length === 0) {
                return false;
            }
            
            const searchTerm = searchQuery.value.trim().toLowerCase();
            return searchResults.value.some(partner => 
                partner.name && partner.name.toLowerCase() === searchTerm
            );
        });

        return {
            searchQuery,
            searchResults,
            isSearching,
            hasSearched,
            hasExactMatch,
            handleSearch,
            selectPartner,
            proceedToCreate
        };
    }
};
</script>

<style scoped>
.partner-search-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 1rem;
}

.search-section {
    margin-bottom: 2rem;
}



.left-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    text-align: center;
}

.left-content h4 {
    color: #333;
    font-size: 1.5rem;
    margin: 0;
}

.search-description {
    color: #666;
    margin: 0;
    font-size: 0.9rem;
}

.search-input-wrapper {
    position: relative;
    width: 100%;
    max-width: 500px;
    display: flex;
    align-items: center;
    margin-top: 0.25rem;
    align-self: center;
}



.search-icon {
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
    z-index: 2;
    font-size: 1.1rem;
    transition: color 0.2s ease;
    pointer-events: none;
}

.search-icon.searching {
    color: #007bff;
    animation: pulse 1.5s infinite;
}

@keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
}

.search-input {
    width: 100%;
    padding: 16px 16px 16px 52px;
    font-size: 1rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    transition: all 0.2s ease;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    color: #333;
    font-family: inherit;
    margin-left: 0;
}

/* PrimeVue input styling */
:deep(.p-inputtext) {
    margin-left: 0 !important;
}

:deep(.p-dropdown) {
    margin-left: 0 !important;
}

.search-input::placeholder {
    color: #999;
    font-size: 0.95rem;
}

.search-input:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
    background-color: #fff;
}

.search-input:hover:not(:readonly) {
    border-color: #007bff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.search-input:readonly {
    background-color: #f8f9fa;
    cursor: default;
    opacity: 0.8;
}

.search-loading {
    text-align: center;
    padding: 2rem;
}

.search-loading p {
    margin-top: 1rem;
    color: #666;
}

.search-results {
    margin-bottom: 2rem;
}

.search-results h5 {
    margin: 0 0 1rem 0;
    color: #333;
    font-size: 1.1rem;
}

.results-list {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    max-height: 400px;
    overflow-y: auto;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.result-item {
    display: flex;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: all 0.2s ease;
}

.result-item:last-child {
    border-bottom: none;
}

.result-item:hover {
    background-color: #f8f9fa;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.result-item:active {
    transform: translateY(0);
}

.partner-info {
    flex: 1;
}

.partner-name {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.25rem;
}

.partner-channel {
    color: #007bff;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.partner-details {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: #666;
    font-size: 0.8rem;
}

.detail-item i {
    font-size: 0.7rem;
}

.select-indicator {
    color: #007bff;
    font-size: 1.2rem;
}

.no-results {
    text-align: center;
    padding: 3rem 1rem;
    color: #666;
    background-color: #f8f9fa;
    border-radius: 8px;
    margin: 1rem 0;
}

.no-results-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.no-results-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.no-results-text {
    font-size: 1rem;
    color: #666;
    margin: 0;
}

.no-results-subtitle {
    font-size: 0.9rem;
    color: #999;
    margin: 0;
}

.single-line-button {
    white-space: nowrap;
    min-width: fit-content;
    margin-top: 1.5rem;
}

/* Responsive design */
@media (max-width: 768px) {
    .partner-search-container {
        padding: 0.5rem;
    }
    

    
    .left-content h4 {
        font-size: 1.3rem;
    }
    
    .search-description {
        font-size: 0.85rem;
    }
    
    .search-input-wrapper {
        max-width: 100%;
    }
    
    .search-input {
        font-size: 0.95rem;
        padding: 14px 14px 14px calc(3rem + 48px);
    }
    
    .search-icon {
        left: calc(3rem + 18px);
        font-size: 1rem;
    }
    

    
    .partner-details {
        flex-direction: column;
        gap: 0.5rem;
    }
    
    .result-item {
        padding: 0.75rem;
    }
}

@media (max-width: 480px) {
    .partner-search-container {
        padding: 0.25rem;
    }
    

    
    .left-content h4 {
        font-size: 1.2rem;
    }
    
    .search-input {
        font-size: 0.9rem;
        padding: 12px 12px 12px calc(3rem + 44px);
    }
    
    .search-icon {
        left: calc(3rem + 16px);
        font-size: 0.9rem;
    }
    

    
    .result-item {
        padding: 0.5rem;
    }
    
    .partner-name {
        font-size: 0.95rem;
    }
    
    .partner-channel {
        font-size: 0.8rem;
    }
}
</style> 